import { Box, Divider, Grid, Typography } from "@mui/material";
import useColors from "../../api/hooks/useColors";
import IconChip from "../../components/IconChip";
import SvgToIcon from "../../icons/SvgToIcon";

// Import icon categories from iconExport
import {
  icons,
  logos,
  severities,
} from "../../scenes/stack/environment/icons/iconExport.jsx";

/**
 * Demonstration of IconChip component showing different variations
 * using actual icons from the codebase
 */
const IconChipDemo = () => {
  const colors = useColors();

  return (
    <Box sx={{ width: "100%" }}>
      {/* Severity Icons */}
      <Typography variant="h6" gutterBottom>
        Severity Icon Chips
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={severities.SeverityBlueIcon}
                width="20px"
                height="20px"
              />
            }
            bgcolor={colors.info_8percent}
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={severities.SeverityWarningType2Icon}
                width="20px"
                height="20px"
              />
            }
            bgcolor={colors.warning_yellow_8percent}
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={severities.SeverityErrorIcon}
                width="20px"
                height="20px"
              />
            }
            bgcolor={colors.error_8percent}
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={severities.SeverityBackgroundSuccessIcon}
                width="20px"
                height="20px"
              />
            }
            bgcolor={colors.success_soft_label_fill}
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={severities.CriticalSeverityIcon}
                width="20px"
                height="20px"
              />
            }
            bgcolor={colors.error_12percent}
          />
        </Grid>
      </Grid>

      {/* Action Icons */}
      <Typography variant="h6" gutterBottom>
        Action Icon Chips
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon IconLogo={icons.LinkIcon} width="20px" height="20px" />
            }
            bgcolor={colors.primary_8percent}
            tooltip="Link"
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={icons.DeleteIcon}
                width="20px"
                height="20px"
              />
            }
            bgcolor={colors.error_8percent}
            tooltip="Delete"
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={icons.SearchIcon}
                width="20px"
                height="20px"
              />
            }
            bgcolor={colors.grey_8percent}
            tooltip="Search"
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={icons.ExportIcon}
                width="20px"
                height="20px"
              />
            }
            bgcolor={colors.info_8percent}
            tooltip="Export"
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon IconLogo={icons.ScanIcon} width="20px" height="20px" />
            }
            bgcolor={colors.success_soft_label_fill}
            tooltip="Scan"
          />
        </Grid>
      </Grid>

      {/* Application Logos */}
      <Typography variant="h6" gutterBottom>
        Application Logo Chips
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={logos.GitHubLogo}
                width="20px"
                height="20px"
              />
            }
            tooltip="GitHub"
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={logos.SlackLogo}
                width="20px"
                height="20px"
              />
            }
            tooltip="Slack"
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon IconLogo={logos.JiraLogo} width="20px" height="20px" />
            }
            tooltip="Jira"
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={logos.GoogleLogo}
                width="20px"
                height="20px"
              />
            }
            tooltip="Google"
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={logos.MicrosoftOffice365Logo}
                width="20px"
                height="20px"
              />
            }
            tooltip="Microsoft Office 365"
          />
        </Grid>
      </Grid>

      {/* Cloud Provider Logos */}
      <Typography variant="h6" gutterBottom>
        Cloud Provider Chips
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon IconLogo={logos.AWSLogo} width="20px" height="20px" />
            }
            bgcolor="#fef6ee"
            tooltip="AWS"
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={logos.GoogleLogo}
                width="20px"
                height="20px"
              />
            }
            bgcolor="#f3f8fe"
            tooltip="Google Cloud"
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={logos.MicrosoftAzureCloudLogo}
                width="20px"
                height="20px"
              />
            }
            bgcolor="#f0f6ff"
            tooltip="Microsoft Azure"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Status Change Icons */}
      <Typography variant="h6" gutterBottom>
        Status Change Icons
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={icons.SnoozedIcon}
                width="20px"
                height="20px"
              />
            }
            bgcolor={colors.grey_8percent}
            tooltip="Snoozed"
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={icons.UnsnoozedIcon}
                width="20px"
                height="20px"
              />
            }
            bgcolor={colors.info_8percent}
            tooltip="Unsnoozed"
          />
        </Grid>
      </Grid>

      {/* Productivity Tools */}
      <Typography variant="h6" gutterBottom>
        Productivity Tool Icons
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={logos.NotionLogo}
                width="20px"
                height="20px"
              />
            }
            bgcolor="#f7f6f3"
            tooltip="Notion"
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={logos.FigmaLogo}
                width="20px"
                height="20px"
              />
            }
            bgcolor="#f3f3f3"
            tooltip="Figma"
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={logos.MondayLogo}
                width="20px"
                height="20px"
              />
            }
            bgcolor="#fdf3f1"
            tooltip="Monday"
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon IconLogo={logos.ZoomLogo} width="20px" height="20px" />
            }
            bgcolor="#eaf5fd"
            tooltip="Zoom"
          />
        </Grid>
      </Grid>

      {/* Size Variations */}
      <Typography variant="h6" gutterBottom>
        Size Variations
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={logos.SalesForceLogo}
                width="16px"
                height="16px"
              />
            }
            sx={{
              width: "24px",
              height: "24px",
            }}
            tooltip="Small"
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={logos.SalesForceLogo}
                width="20px"
                height="20px"
              />
            }
            tooltip="Medium (Default)"
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={logos.SalesForceLogo}
                width="24px"
                height="24px"
              />
            }
            sx={{
              width: "40px",
              height: "40px",
            }}
            tooltip="Large"
          />
        </Grid>
      </Grid>

      {/* Custom Styling */}
      <Typography variant="h6" gutterBottom>
        Custom Styling
      </Typography>
      <Grid container spacing={2}>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon IconLogo={logos.OktaLogo} width="20px" height="20px" />
            }
            boxShadow="0px 2px 4px rgba(0, 0, 0, 0.25)"
            tooltip="With box shadow"
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={logos.ServiceNowLogo}
                width="20px"
                height="20px"
              />
            }
            bgcolor={colors.primary_8percent}
            shadowColor={colors.primary_dark}
            tooltip="With custom shadow color"
          />
        </Grid>
        <Grid item>
          <IconChip
            icon={
              <SvgToIcon
                IconLogo={logos.BreezeLogo}
                width="20px"
                height="20px"
              />
            }
            sx={{
              borderRadius: "4px",
              border: `1px solid ${colors.primary_12percent}`,
            }}
            tooltip="With custom border"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default IconChipDemo;
